import { useMemo, type ChangeEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import { typographyVariants } from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';

import type { PrivateBucket } from '../../../helpers/knowledge-base/fileSystemListing.js';
import {
  ROOT_FOLDER_ID,
  USERS_PRIVATE_FOLDERS_ID,
} from '../../../types/fileSystem.js';
import { FileSystemBreadcrumbs } from '../FileSystem/Breadcrumbs/Breadcrumbs.jsx';
import { UsersPrivateFoldersTable } from './UsersPrivateFoldersTable.jsx';

type Props = {
  buckets: PrivateBucket[];
  isLoading: boolean;
  searchValue: string;
  search: string;
  onChangeSearch: (event: ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  onOpenFolder: (folderId: string) => void;
};

const NoPrivateFoldersState = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2">
      <Illustration
        className="size-40"
        data-testid="no-private-folders-icon"
        component="IllustrationEmptyState"
      />
      <p className={typographyVariants({ variant: 'h4' })}>
        {t('No users private folders yet')}
      </p>
      <p
        className={typographyVariants({
          variant: 'bodyShort01',
          color: 'secondary',
        })}
      >
        {t('Private folders of workspace users will appear here')}
      </p>
    </div>
  );
};

const UsersPrivateFolders = ({
  buckets,
  isLoading,
  searchValue,
  search,
  onChangeSearch,
  onClearSearch,
  onOpenFolder,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const breadcrumbItems = useMemo(
    () => [
      { folderId: ROOT_FOLDER_ID, name: t('My Files') },
      { folderId: USERS_PRIVATE_FOLDERS_ID, name: t('Users private folders') },
    ],
    [t],
  );

  const query = search.trim().toLowerCase();

  const visibleBuckets = useMemo(
    () => buckets.filter((bucket) => bucket.name.toLowerCase().includes(query)),
    [buckets, query],
  );

  const showEmptyState = !isLoading && !buckets.length && !query;

  return (
    <>
      <FileSystemBreadcrumbs
        items={breadcrumbItems}
        onFolderChange={onOpenFolder}
      />

      <div className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div className="flex flex-row items-center gap-2">
          <Information className="size-4 fill-text-secondary" />
          <p
            className={typographyVariants({
              variant: 'bodyShort01',
              color: 'secondary',
            })}
          >
            {t('Browse private folders of workspace users')}
          </p>
        </div>

        <SearchBar
          placeholder={t('Search a file or a folder')}
          className="h-8 w-75 max-w-75"
          onChange={onChangeSearch}
          onClear={onClearSearch}
          value={searchValue}
        />
      </div>

      {showEmptyState ? (
        <NoPrivateFoldersState />
      ) : (
        <UsersPrivateFoldersTable
          buckets={visibleBuckets}
          onOpenBucket={onOpenFolder}
        />
      )}
    </>
  );
};

export { UsersPrivateFolders };
